/**
 * Client-side report-view renderer (three.js, in the browser).
 *
 * This and SSE's `captureReportViews` (apps/surgical-suite-engine MeshViewer.jsx)
 * must render the same look, and both are pinned to `shared/report-look.json` by
 * sidecar/tests/test_report_look_parity.py — including the matcap asset, by hash.
 *
 * ⚠ THE HEADER HERE USED TO ASSERT THE TWO "MATCH EXACTLY" AS PROSE, and stayed that
 * way for weeks after they stopped matching: this file grew a matcap, GTAO, an
 * orthographic camera and creased normals, and SSE got none of them. Measured off the
 * two delivered PDFs for O3D-2026-08-01-0004, mean bone pixel: #C9BD9C here, #685F58
 * there. A comment cannot hold two implementations together; the spec and its parity
 * test can. If you change the look, change the spec in the same commit.
 *
 * Renders the 6 orthographic anatomical views labelled A/P/LL/RL/S/I + an
 * unlabelled isometric, at 1600² on a white or black background, with the SSE
 * "Report look" material/lighting (Bone tone + Shading + Finish). Each ortho gets
 * the SSE per-image tiled "Osteo3d ®" watermark + top-right corner label (ported
 * from SSE screenshot.js `composeToDataURL`). Also computes mesh metrics.
 *
 * three + loaders are lazy-imported; DRACO decoder is vendored at /draco/.
 */

// Camera direction (mesh centre → camera) + up, per SSE view. Mesh-native frame
// (raw STL/OBJ from CT is Z-up). Labels use SSE's A/P/LL/RL/S/I.
// Mesh frame is RAS-like (anterior = +Y, right = +X, superior = +Z): the camera
// sits on the +Y side to SEE the anterior face. (A/P were flipped when assuming
// LPS; L/R and S/I are correct.)
// Default frame assumes anterior = -Y (LPS/CT-STL, matches the packaging reference).
// Files whose anterior is +Y (RAS/OBJ) pass flipAP:true to swap A/P (+ISO) so
// "Anterior" always shows the face.
import { hasPolypaint, parseZBrushObj, hasMeaningfulPaint } from './objPolypaint.js'

const VIEW_DEFS = [
  ['A',  { dir: [0, -1, 0], up: [0, 0, 1] }],  // Anterior  (front)
  ['P',  { dir: [0, 1, 0],  up: [0, 0, 1] }],  // Posterior (back)
  ['LL', { dir: [-1, 0, 0], up: [0, 0, 1] }],  // Left Lateral
  ['RL', { dir: [1, 0, 0],  up: [0, 0, 1] }],  // Right Lateral
  ['S',  { dir: [0, 0, 1],  up: [0, 1, 0] }],  // Superior  (top)
  ['I',  { dir: [0, 0, -1], up: [0, 1, 0] }],  // Inferior  (bottom)
]
const ISO_DEF = { dir: [1, -1, 0.8], up: [0, 0, 1] }

/**
 * Extra cameras a CALLER may ask for, beyond VIEW_DEFS and ISO.
 *
 * ⚠️ Deliberately NOT in VIEW_DEFS. Every consumer of renderReport iterates that
 * table, so an entry there would make the STL/OBJ tool and the Packaging
 * Illustration start emitting an extra view — and the packaging label must stay
 * byte-identical. Same reasoning as `smoothNormals` and `labelRelief` above:
 * opt-in, default off, proven by the render hashes not moving.
 *
 * `ISO2` is a SECOND oblique of the same scene, so one page can show it from two
 * angles. It self-fits like ISO rather than sharing the orthographic fit scale —
 * it is a different framing of the object, not another member of the six-view
 * set.
 *
 * 🔴 ITS DIRECTION IS NOT SETTLED, and no page currently uses it. Three were
 * rendered on the real case and all three are unusable, because the orbital
 * volumes are ENCLOSED — only a camera that looks steeply into the export's
 * cut-away top sees them at all:
 *
 *   [-1, -1, 0.8]   mirrored isometric — the export is a cropped midface SLAB,
 *                   so from the other side the camera looks into its hollow
 *                   back and the volumes are a few specks
 *   [0.45, -1, 0.25] lower and frontal — the orbital rims occlude them
 *   [0.8, -1, 1.6]   steeper — becomes a superior view of the skull base
 *
 * An attempt was then made to MEASURE the answer rather than guess again — sweep
 * 37 directions, count how many volume-coloured pixels survive occlusion in
 * each, take the most visible view that is also angularly distinct from ISO. It
 * was abandoned, and the reason is worth recording: reproducing the report's
 * render outside the report's pipeline is its own project. The probe called
 * renderReport directly, without REPORT_RENDER_OPTS, sceneFrameOpts or the
 * fit-scale override that renderScenes supplies, and its output looked nothing
 * like the page — unlit, differently framed, and its own ISO-equivalent bore no
 * resemblance to the real one. A sweep over the wrong pipeline measures nothing,
 * and its confident-looking numbers (posterior views scoring 20-170x the
 * anterior ones) would have sent the camera to the back of the skull.
 *
 * Which second angle the figure should use is a judgement about a clinical
 * picture, and it belongs to the design team. The machinery is finished and
 * tested; the direction is one line of data when they say what the second view
 * is meant to show. Until then the volume page ships two pictures and says so.
 *
 * A view where the subject is invisible is worse than no view.
 */
const EXTRA_VIEW_DEFS = {
  ISO2: { dir: [0.8, -1, 1.6], up: [0, 0, 1] },
}

/** Which extra codes exist, for callers that want to check before asking. */
export const EXTRA_VIEWS = Object.keys(EXTRA_VIEW_DEFS)

/**
 * The camera definition a view actually uses, after the frame options.
 *
 * `flipAP` swaps only the A/P (and ISO) camera sides — the historical toggle for the team's
 * ZBrush/RAS exports (anterior = +Y), whose left/right already matched VIEW_DEFS.
 *
 * `lpsFrame` is for meshes in the CT's own LPS frame — the planning engine's exports. PROVEN on
 * a real case, not assumed: the plan's 5 mm maxillary advance displaced the upper teeth along −Y
 * (anterior = −Y), and the chin its analysis measured 1.4 mm to the patient's RIGHT sits at
 * −1.42 mm X (right = −X). For such meshes the BASE A/P cameras are already correct (flipAP must
 * stay off), but the laterals are crossed — the LL camera would sit on the patient's RIGHT — and
 * the S/I images come out spun 180°. So: negate dir.x (moves each lateral camera to the side its
 * badge names; A/P/S/I dirs have x = 0 and are untouched) and negate up.y (rights the S/I
 * in-plane orientation; the other views' up is +Z and untouched). This only MOVES cameras — the
 * render stays an honest photograph of the true mesh, so chirality cannot flip.
 *
 * Exported for the tests: the pixel outcome follows deterministically from the camera position,
 * so the left/right claim is tested HERE, where it is checkable, rather than by eyeballing a
 * profile that looks the same from either side.
 */
export function resolveViewDef(def, { flipAP = false, lpsFrame = false } = {}, code = '') {
  const neg = (v) => (v === 0 ? 0 : -v)   // never emit -0: it breaks strict comparisons downstream
  let d = def
  if (flipAP && (code === 'A' || code === 'P' || code === 'ISO')) {
    d = { ...d, dir: [d.dir[0], neg(d.dir[1]), d.dir[2]] }
  }
  if (lpsFrame) {
    d = {
      dir: [neg(d.dir[0]), d.dir[1], d.dir[2]],
      up: [d.up[0], neg(d.up[1]), d.up[2]],
    }
  }
  return d
}

export const VIEW_LABELS = {
  A: 'Anterior', P: 'Posterior', LL: 'Left Lateral', RL: 'Right Lateral',
  S: 'Superior', I: 'Inferior', ISO: 'Isometric',
}

// SSE Report-look "Shading" endpoints (t=0 soft … t=1 hard), lerped by shadingT.
const SOFT = { ambient: 0.52, keyMul: 1.05, hemi: 0.24, exposure: 1.04 }
const HARD = { ambient: 0.14, keyMul: 2.10, hemi: 0.10, exposure: 0.80 }
const clamp01 = (v) => Math.min(1, Math.max(0, v))
// Faces meeting at a sharper angle than this keep split (hard) normals. 30° keeps
// engraved lettering crisp while still smoothing CT/ZBrush surface tessellation.
const CREASE_ANGLE = Math.PI / 6

let _mod = null
async function three() {
  if (_mod) return _mod
  const THREE = await import('three')
  const [{ STLLoader }, { OBJLoader }, { GLTFLoader }, { DRACOLoader }, { toCreasedNormals }] = await Promise.all([
    import('three/addons/loaders/STLLoader.js'),
    import('three/addons/loaders/OBJLoader.js'),
    import('three/addons/loaders/GLTFLoader.js'),
    import('three/addons/loaders/DRACOLoader.js'),
    // toCreasedNormals: welds + computes normals with a CREASE ANGLE.
    import('three/addons/utils/BufferGeometryUtils.js'),
  ])
  _mod = { THREE, STLLoader, OBJLoader, GLTFLoader, DRACOLoader, toCreasedNormals }
  return _mod
}

// Post-processing (ambient occlusion) — lazy-loaded only when `shadows` is on, so
// the default render path stays light. GTAO derives depth+normals from its own pass
// → it darkens crevices over ANY surface material (incl. the matcap) and works with
// the OrthographicCamera. Reproduces the ZBrush BPR crevice shadows we can't get from
// a matcap alone (a matcap bakes a fixed lit sphere, so it cannot self-occlude).
let _pp = null
async function postFx() {
  if (_pp) return _pp
  const [{ EffectComposer }, { RenderPass }, { GTAOPass }, { OutputPass }] = await Promise.all([
    import('three/addons/postprocessing/EffectComposer.js'),
    import('three/addons/postprocessing/RenderPass.js'),
    import('three/addons/postprocessing/GTAOPass.js'),
    import('three/addons/postprocessing/OutputPass.js'),
  ])
  _pp = { EffectComposer, RenderPass, GTAOPass, OutputPass }
  return _pp
}

const extOf = (name) => (name || '').toLowerCase().match(/\.(stl|obj|glb|gltf)$/)?.[1] || 'stl'

// Load a ZBrush-style matcap (lit-sphere) texture; returns null if missing so the
// renderer falls back to the lit MeshStandardMaterial. A matcap encodes material +
// lighting, so MeshMatcapMaterial reproduces the ZBrush look exactly (view-space).
//
// CRITICAL: MeshMatcapMaterial samples uv = viewNormal.xy·0.5+0.5, so steep/grazing
// normals sample the texture EDGE. If the sphere is inscribed with a black margin
// (like this ZBrush export), those normals hit BLACK → dark rims + coarse black
// speckle on rough meshes. So we make it FULL-BLEED: auto-detect the sphere's
// non-black bbox, crop to it, scale to fill, and clamp the edges → grazing normals
// sample the sphere's ivory edge (soft shadow) instead of black.
async function loadMatcap(THREE, url) {
  try {
    const img = await new Promise((res, rej) => {
      const im = new Image(); im.crossOrigin = 'anonymous'
      im.onload = () => res(im); im.onerror = rej; im.src = url
    })
    const w = img.naturalWidth, h = img.naturalHeight
    const src = document.createElement('canvas'); src.width = w; src.height = h
    const sctx = src.getContext('2d'); sctx.drawImage(img, 0, 0)
    const data = sctx.getImageData(0, 0, w, h).data
    // bbox of the sphere (non-black, opaque) pixels
    let minX = w, minY = h, maxX = 0, maxY = 0
    const THRESH = 26
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const i = (y * w + x) * 4
        if (data[i + 3] > 12 && (data[i] + data[i + 1] + data[i + 2]) / 3 > THRESH) {
          if (x < minX) minX = x; if (x > maxX) maxX = x
          if (y < minY) minY = y; if (y > maxY) maxY = y
        }
      }
    }
    if (maxX <= minX || maxY <= minY) { minX = 0; minY = 0; maxX = w - 1; maxY = h - 1 }
    // square crop centred on the sphere, inset 1.5% to drop the antialiased dark rim
    const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2
    const side = Math.max(maxX - minX + 1, maxY - minY + 1) * 0.97
    const sx = cx - side / 2, sy = cy - side / 2
    const S = 512
    const out = document.createElement('canvas'); out.width = S; out.height = S
    out.getContext('2d').drawImage(src, sx, sy, side, side, 0, 0, S, S)
    const tex = new THREE.CanvasTexture(out)
    tex.colorSpace = THREE.SRGBColorSpace
    tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping
    tex.needsUpdate = true
    // The colour a FLAT, CAMERA-FACING surface gets from this matcap: uv is
    // viewNormal.xy·0.5+0.5, so such a surface samples the texture centre. The engraved
    // label plate is exactly that surface, and it has to be rendered with a LIT material
    // (a matcap cannot show face-on relief), so this is what its lit result must match.
    //
    // Without it the plate inherits only the group tint while the bone gets tint × matcap
    // — warmth applied twice — which is why the plate came out 38 levels too blue and read
    // as washed-out grey against cream bone. Measured on the team's render: bone B/R 0.645
    // vs plate 0.813, where ZBrush keeps the two within 0.001 of each other.
    const oc = out.getContext('2d')
    let cr = 0, cg = 0, cb = 0, cn = 0
    const box = oc.getImageData(S / 2 - 8, S / 2 - 8, 16, 16).data
    for (let i = 0; i < box.length; i += 4) { cr += box[i]; cg += box[i + 1]; cb += box[i + 2]; cn++ }
    tex.userData.faceOnRGB = [cr / cn / 255, cg / cn / 255, cb / cn / 255]
    return tex
  } catch { return null }
}

/**
 * A NEUTRAL lit-sphere, derived from the bone one rather than shipped beside it.
 *
 * WHY THIS EXISTS. Splints and guides are fabricated in white resin and the team
 * reported they were not rendering white. They could not. MeshMatcapMaterial
 * MULTIPLIES its texture by the material colour, so the bone matcap's own cream —
 * measured face-on at #D3C8A7 — is a ceiling: nothing rendered through it can be
 * brighter or less warm than that. The splint OBJs already carry pure-white ZBrush
 * polypaint and still came out cream, which is the proof that no colour change
 * could have fixed it. A second lit-sphere is the only thing that can.
 *
 * DERIVED, not a second asset. Desaturating the cropped bone matcap keeps the
 * lighting GEOMETRY identical — same key direction, same terminator, same rim
 * falloff — so a splint and the bone beside it on one page are lit from the same
 * place. A third-party white lit-sphere bakes a different light position and the
 * two would disagree, subtly, on a document a surgeon reads. It also means there
 * is no second file to pin by sha256 and nothing that can drift from skeleton.png.
 *
 * Same argument as labelSurfaceColor(), which derives the label plate's colour
 * from the matcap for exactly this reason instead of hardcoding one.
 *
 * `targetFaceOn` is deliberately NOT 1.0. A paper-white object on a white sheet
 * loses its silhouette; light resin grey keeps the form readable and is what the
 * printed part actually looks like.
 */
function deriveNeutralMatcap(THREE, tex, targetFaceOn = 236 / 255) {
  try {
    const src = tex?.image
    if (!src?.width) return null
    const S = src.width
    const c = document.createElement('canvas'); c.width = S; c.height = S
    const ctx = c.getContext('2d')
    ctx.drawImage(src, 0, 0)
    const img = ctx.getImageData(0, 0, S, S)
    const d = img.data
    // Scale from the SOURCE's own face-on luminance, so the neutral sphere tracks
    // the bone one if that is ever re-authored instead of silently drifting.
    const [fr, fg, fb] = tex.userData?.faceOnRGB ?? [0.83, 0.78, 0.65]
    const faceLuma = 0.2126 * fr + 0.7152 * fg + 0.0722 * fb
    const k = faceLuma > 0 ? targetFaceOn / faceLuma : 1
    for (let i = 0; i < d.length; i += 4) {
      const l = (0.2126 * d[i] + 0.7152 * d[i + 1] + 0.0722 * d[i + 2]) * k
      const v = l > 255 ? 255 : l
      d[i] = v; d[i + 1] = v; d[i + 2] = v
    }
    ctx.putImageData(img, 0, 0)
    const out = new THREE.CanvasTexture(c)
    out.colorSpace = THREE.SRGBColorSpace
    out.wrapS = out.wrapT = THREE.ClampToEdgeWrapping
    out.needsUpdate = true
    out.userData.faceOnRGB = [targetFaceOn, targetFaceOn, targetFaceOn]
    return out
  } catch { return null }
}

async function loadMeshes({ THREE, STLLoader, OBJLoader, GLTFLoader, DRACOLoader }, buf, ext,
  { usePolypaint = false } = {}) {
  const meshes = []
  if (ext === 'stl') {
    meshes.push(new THREE.Mesh(new STLLoader().parse(buf)))
  } else if (ext === 'obj') {
    const text = new TextDecoder().decode(buf)
    // ZBrush polypaint travels in `#MRGB` comment lines, which every OBJ loader
    // ignores — so the team's own case-report colours are in their files and the
    // portal never saw them. Decoded from real exports they are byte-exact
    // matches for the official palette.
    //
    // OPT-IN, DEFAULT OFF (Deepak, 2026-07-31). The team has a tuned
    // Colours-panel workflow and this changes what they see, so it does not
    // switch on under them: with `usePolypaint` false the file goes through
    // OBJLoader exactly as it always has. tools/render-hash proves the default
    // path is byte-identical.
    if (usePolypaint && hasPolypaint(text)) {
      const { groups, paintedVertices, palette } = parseZBrushObj(text)
      if (groups.length && hasMeaningfulPaint(palette, paintedVertices)) {
        for (const g of groups) {
          const geom = new THREE.BufferGeometry()
          geom.setAttribute('position', new THREE.BufferAttribute(g.position, 3))
          if (g.color) geom.setAttribute('color', new THREE.BufferAttribute(g.color, 3))
          const m = new THREE.Mesh(geom)
          m.name = g.name
          m.userData.polypaint = Boolean(g.color)
          meshes.push(m)
        }
        return meshes
      }
    }
    const obj = new OBJLoader().parse(text)
    obj.traverse((o) => { if (o.isMesh) meshes.push(o) })
  } else {
    const draco = new DRACOLoader().setDecoderPath('/draco/')
    const loader = new GLTFLoader().setDRACOLoader(draco)
    const gltf = await new Promise((res, rej) => loader.parse(buf, '', res, rej))
    gltf.scene.updateMatrixWorld(true)
    gltf.scene.traverse((o) => { if (o.isMesh) { o.geometry.applyMatrix4(o.matrixWorld); meshes.push(o) } })
    draco.dispose()
  }
  return meshes
}

/**
 * Load a multi-file SCENE: several meshes composed into one view, each carrying
 * its own colour.
 *
 * The case report needs this everywhere the shipped reports show a cream
 * cranium with a blue maxilla, a green mandible and a magenta genioplasty
 * segment. Two shapes have to work, because the team produces both:
 *
 *   · one OBJ with ZBrush polygroups   → coloured by group (the existing path)
 *   · one file per part                → coloured per file (this path)
 *
 * A part's colour is stamped onto its meshes as `userData.partColor`, which the
 * material loop prefers over the group lookup. That keeps the whole downstream
 * pipeline — materials, matcap, culling, camera fit, stamping — untouched, and
 * means the Packaging Illustration (which never passes parts) cannot move.
 *
 * @param {Array<{bytes:ArrayBuffer|Uint8Array, filename:string, color?:string,
 *                opacity?:number, visible?:boolean}>} parts
 */
async function loadScene(mod, parts, opts = {}) {
  const meshes = []
  for (const part of parts) {
    if (part.visible === false) continue
    const buf = part.bytes instanceof ArrayBuffer
      ? part.bytes
      : part.bytes.buffer.slice(part.bytes.byteOffset, part.bytes.byteOffset + part.bytes.byteLength)
    const loaded = await loadMeshes(mod, buf, extOf(part.filename), opts)
    if (!loaded.length) {
      // Loud, not silent. A part that contributes nothing to a surgical figure
      // must not simply be absent from it.
      throw new Error(`No mesh geometry found in "${part.filename}"`)
    }
    for (const m of loaded) {
      // Per-POLYGROUP declarations win over the part's own, because that is the
      // finer statement: the team exports a guide and the model it sits on as ONE
      // file with two polygroups, so a single value per file can only ever make
      // both of them right or both of them wrong. It made them wrong — pp.20/21
      // put the anatomy inside a guide's file in white. Keyed by the `g` name,
      // which both loaders preserve.
      m.userData.partColor = part.groups?.[m.name] ?? part.color ?? null
      m.userData.partOpacity = part.opacity ?? 1
      // Fabricated appliance rather than anatomy — renders against the neutral
      // lit-sphere. Set by the classifier (caseReport/draft.js), never guessed here.
      m.userData.partNeutral = Boolean(part.neutralGroups?.[m.name] ?? part.neutral)
      // So an operator-marked stamp can be addressed by name on the parts path too.
      m.userData.partName = part.filename ?? null
      meshes.push(m)
    }
  }
  return meshes
}

// Mesh metrics from the loaded geometry (SSE Page 3). Volume via signed tetrahedra;
// watertight is left null (a reliable manifold check needs position-welding — too
// costly in-browser; the report shows "—", same as SSE when it can't analyze).
function computeMetrics(meshes, THREE) {
  let faces = 0, vertices = 0, volume = 0
  const box = new THREE.Box3()
  const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3()
  for (const m of meshes) {
    const g = m.geometry
    const pos = g.attributes.position
    if (!pos) continue
    vertices += pos.count
    const idx = g.index
    faces += idx ? idx.count / 3 : pos.count / 3
    box.expandByObject(m)
    const at = (i) => idx ? idx.getX(i) : i
    const n = idx ? idx.count : pos.count
    for (let i = 0; i + 2 < n; i += 3) {
      a.fromBufferAttribute(pos, at(i)); b.fromBufferAttribute(pos, at(i + 1)); c.fromBufferAttribute(pos, at(i + 2))
      volume += a.dot(b.clone().cross(c)) / 6
    }
  }
  const size = box.isEmpty() ? new THREE.Vector3() : box.getSize(new THREE.Vector3())
  return {
    faces: Math.round(faces),
    vertices,
    bbox_mm: [size.x, size.y, size.z],
    volume_cm3: Math.abs(volume) / 1000, // mm³ → cm³
    watertight: null,
  }
}

export const EDGE_BAND = {
  // Stroke width in pixels, estimated as 2 * area / perimeter. Below this the band meets itself
  // and fills the glyph instead of edging it — measured fraction of a stroke's centre darkened
  // at sigma 1: 10.5 px -> 0.000, 6 px -> 0.008, 4 px -> 0.094, 2.6 px -> 0.384, 2 px -> 0.635.
  // A case ID rendered as a dark smear is worse than one rendered faintly, and unlike a faint
  // one it cannot be recovered by looking harder.
  //
  // The estimator is a RADIUS for compact shapes (a disc of radius r reads r, a square of side
  // s reads s/2) and reads high on rotated glyphs, because the 4-connected perimeter count
  // under-reads a 45-degree edge by about sqrt(2). Both errors push toward admitting, so this
  // is a floor with margin rather than a measurement to lean on.
  minStrokePx: 4,
  // Band radius as a fraction of stroke width, from the ladder that was printed: 1 px against
  // ~10.5 px strokes read as engraving where 2 px read as an embossed label.
  //
  // PER VIEW, deliberately. A groove seen obliquely IS narrower in pixels, so its edge should
  // be too; one width across a plate would make the oblique views read heavier than the face-on
  // one. This is NOT the sharedFitScale case, which exists so the model's SCALE is comparable
  // between views — here the feature's apparent size is the thing being rendered.
  outlineOfStroke: 1 / 10.5,
}

/**
 * Should the marked group get a letter outline, and if not, why not?
 *
 * THERE IS NO SHAPE TEST HERE, and its absence is the decision. Three were written and all
 * three were wrong, each in review:
 *
 *   boundary/covered   scale-dependent    — 0.540 at res 800 vs 0.200 at 2400 on ONE geometry,
 *                                           colliding with a solid slab under ~67 px across
 *   covered/bbox       rotation-dependent — a plate tilted past ~7 degrees reads as lettering,
 *                                           and the ranges overlap real slabs outright
 *   perimeter^2/area   count-dependent    — scales linearly with the number of glyphs, so a
 *                                           long case ID passes at ~900 and a four-character
 *                                           one lands at ~150, on the threshold
 *
 * That is the same wall `stampGroup` exists because of: this renderer already records that a
 * stamp on a curved skull is measurably LESS plate-like than a cutting guide, and that two
 * attempts to separate them by shape each shipped a clinical defect. Marking is the answer to
 * exactly this question, and adding a fourth heuristic behind the operator's explicit choice
 * would be re-deciding what they already decided — with a threshold nobody can see.
 *
 * So the mark is trusted. What remains is the failure the operator CANNOT see coming: strokes
 * too fine to carry a band, which render as a smear rather than as text. That is measurable
 * without guessing intent, so that is what is guarded.
 *
 * The accepted risk, stated: marking a solid plate traces its rim. It is visible in the preview
 * before anything is downloaded, resolveStampMeshes already refuses a pick above 25% of the
 * model, and the operator chose the group. A rim on a plate they marked is a mis-click they can
 * see and undo, not a silent wrong answer.
 *
 * @returns {{draw: boolean, reason: 'lettering'|'empty'|'too-fine', strokePx: number,
 *            outlinePx: number, covered: number}}
 */
export function edgeDecision({ covered, boundary }) {
  const no = (reason, extra = {}) => ({
    draw: false, reason, strokePx: 0, outlinePx: 0, covered: covered || 0, ...extra,
  })
  if (!(covered > 0) || !(boundary > 0)) return no('empty')
  const strokePx = (2 * covered) / boundary
  if (strokePx < EDGE_BAND.minStrokePx) return no('too-fine', { strokePx })
  return {
    draw: true,
    reason: 'lettering',
    strokePx,
    covered,
    outlinePx: Math.max(1, Math.round(strokePx * EDGE_BAND.outlineOfStroke)),
  }
}

/**
 * One answer for a whole report from the per-view decisions.
 *
 * KEYED ON THE VIEW THAT MATTERS, which is the one where the stamp presents the most pixels —
 * the face-on view the lettering is authored for. The first version asked only whether ANY view
 * drew an outline, and a review showed that silences the case that counts: on a six-view plate
 * the obliques can draw while the face-on view refuses, and the operator is told nothing about
 * the only view they were going to check.
 *
 * Views that never saw the stamp are ignored rather than reported. A stamp sits on one aspect of
 * the model, so some views legitimately see nothing; counting those as refusals would fire a
 * warning on every correct render, and a warning that cries wolf is worse than none.
 */
export function edgeSummary(decisions) {
  const seen = (decisions ?? []).filter((d) => d && d.reason !== 'empty')
  if (!seen.length) return { drawn: 0, refused: null, refusedView: null }
  const drawn = seen.filter((d) => d.draw).length
  const main = seen.reduce((a, b) => ((b.covered || 0) > (a.covered || 0) ? b : a))
  return {
    drawn,
    refused: main.draw ? null : main.reason,
    refusedView: main.draw ? null : (main.code ?? null),
  }
}

/**
 * Coverage and boundary length of a mask's alpha channel.
 *
 * Separate from the compositor so the decision can be taken — and REPORTED — before any
 * pixels are touched. It also short-circuits the empty case, which is most views: the stamp
 * is on the occiput, so five of six views see nothing and used to pay for a full 2.56M-pixel
 * neighbour scan to discover it.
 */
export function maskShape(data, w, h) {
  let covered = 0, boundary = 0
  const cov = new Uint8Array(w * h)
  for (let j = 0; j < w * h; j++) if (data[j * 4 + 3] > 127) { cov[j] = 1; covered++ }
  // Pure optimisation, deliberately unobservable: the neighbour scan below would return the
  // same zeros. No test can catch its removal, and that is the point of saying so here.
  if (!covered) return { covered: 0, boundary: 0 }
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const j = y * w + x
      if (!cov[j]) continue
      // A frame-border pixel counts as boundary. A stamp clipped by the frame therefore reads
      // slightly finer than it is, which errs toward refusing — the safe direction, since the
      // only thing refusing can cost is an outline the operator is told about.
      if (x === 0 || y === 0 || x === w - 1 || y === h - 1
        || !cov[j - 1] || !cov[j + 1] || !cov[j - w] || !cov[j + w]) boundary++
    }
  }
  return { covered, boundary }
}

/**
 * Draw a dark edge along each engraved letter, so the stamp stops blending into the bone.
 *
 * WHY NOT THE EXISTING MACHINERY. Ambient occlusion cannot do it — GTAO was swept from radius
 * 0.005 to 0.15 and every setting rendered identically, because AO cannot resolve a 0.43 mm
 * step. Neither can shadow: the key's own shadow is ~0.5 mm long at the shipped angles, and
 * raising the elevation to lengthen it drops the lit plate 220 -> 199 on a curved stamp, i.e.
 * it buys legibility by turning the stamp into a patch. Both measured, both rejected.
 *
 * WHY NOT EDGE DETECTION EITHER. The first version of this rendered the stamp as view-space
 * normals and kept the local excess, by analogy with the relief pass. It was a no-op, and
 * measuring said so before any of it was believed: across gains of 1/20 to 1/160 the rendered
 * contrast was 21.54 every time, identical to the pass being switched off. Two reasons, both
 * visible only once the mask was dumped and looked at — the interior-only test eroded thin
 * glyphs away to 13% of their pixels, and the surviving normal deviation was mean 2.07 of 255
 * where the arithmetic needed tens. The mask showed why: there is no plate to detect edges
 * ON. The marked group IS the lettering.
 *
 * So the outline is not derived, it is simply the mask's own boundary — exact, and free.
 * `edgeDecision` above is what keeps that honest: it refuses a slab and refuses strokes too
 * fine to carry a band, and it says which, so a missing outline is never silent.
 *
 * THE BAND TOUCHES BONE, about 2 px either side of the glyph, and that is deliberate rather
 * than overlooked. Measured at sigma 1, outward from the edge: 0.5 px darkens 34% at strength
 * 0.55, 1 px 17%, 2 px 2.6%, 3 px nothing. At the reference framing that is ~0.3 mm of
 * surrounding bone. It is stated because bone darkening is exactly what the team objected to on
 * 2026-08-14, and the next person to ask should find the number rather than re-derive it.
 *
 * BOUNDED, not merely small. With GTAO off so the two frames are deterministic, the whole
 * Posterior view differs in 31,260 pixels and EVERY ONE is within 4 px of a letter — zero
 * beyond it, at any threshold. Measure it that way if this is ever questioned: a mean over a
 * tile dilutes by area, and with GTAO on the renderer's own non-determinism swamps the answer
 * (0.058 per channel outside the stamp, which is noise and not this pass).
 */
function letterEdges(ctx, { edgeMask, edgeStrength, outlinePx, w, h }) {
  // Soft band straddling the glyph edge. The blurred coverage is ~1 well inside a stroke and
  // ~0 well outside it, so `1 - |2b - 1|` peaks exactly on the boundary and falls to zero both
  // ways — a groove edge, not a halo on one side and not a fill.
  const blur = document.createElement('canvas')
  blur.width = w; blur.height = h
  const bctx = blur.getContext('2d')
  bctx.filter = `blur(${outlinePx}px)`
  bctx.drawImage(edgeMask, 0, 0, w, h)
  bctx.filter = 'none'
  const b = bctx.getImageData(0, 0, w, h).data
  const img = ctx.getImageData(0, 0, w, h)
  const d = img.data
  for (let i = 0; i < d.length; i += 4) {
    const bc = b[i + 3] / 255
    if (bc <= 0 || bc >= 1) continue
    const band = 1 - Math.abs(2 * bc - 1)
    if (band <= 0) continue
    const mul = 1 - edgeStrength * band
    d[i] *= mul; d[i + 1] *= mul; d[i + 2] *= mul
  }
  ctx.putImageData(img, 0, 0)
}

// Port of SSE screenshot.js composeToCanvas (tileWatermark + cornerLabel path):
// stamp a tiled "Osteo3d ®" watermark + a top-right anatomical label onto a copy
// of the GL frame; return base64 PNG (no data: prefix).
function stampView(src, { lightBg, bg, cornerLabel, subLabel, watermark = true, reliefMask = null, reliefStrength = 0, edgeMask = null, edgeStrength = 0, edgeOutlinePx = 0 }) {
  const w = src.width, h = src.height
  const off = document.createElement('canvas')
  off.width = w; off.height = h
  const ctx = off.getContext('2d')
  // Fill the background in 2D FIRST (not tone-mapped), then composite the render over
  // it. In the shadows path the GL render is transparent-bg (see renderReport) so the
  // EffectComposer's OutputPass can't darken the background — white stays white here
  // regardless of matcap brightness/exposure. For opaque renders the fill is covered.
  if (bg) { ctx.fillStyle = bg; ctx.fillRect(0, 0, w, h) }
  ctx.drawImage(src, 0, 0, w, h)
  // Engraving relief, applied BEFORE the watermark and labels so those stay clean.
  //
  // reliefMask holds the raw self-shadow amount. Multiplying it in directly does not work:
  // the skull casts metre-scale shadows onto its own flank and onto the mandible, and on the
  // real case that blackened the left parietal and the entire mandible. Those shadows are
  // LOW frequency; engraved lettering is high frequency. So subtract a blurred copy and keep
  // only the local excess — a broad shadow cancels against its own blur, a 0.4 mm letter edge
  // does not. Cheaper and better-behaved than a Sobel on depth, which would outline every
  // foramen and every piece of CT staircase noise.
  if (reliefMask && reliefStrength > 0) {
    const blur = document.createElement('canvas')
    blur.width = w; blur.height = h
    const bctx = blur.getContext('2d')
    // Radius must exceed a letter stroke but stay far below the cast-shadow features.
    bctx.filter = `blur(${Math.max(2, Math.round(w * 0.010))}px)`
    bctx.drawImage(reliefMask, 0, 0, w, h)
    bctx.filter = 'none'
    const s = reliefMask.getContext('2d').getImageData(0, 0, w, h).data
    const b = bctx.getImageData(0, 0, w, h).data
    const img = ctx.getImageData(0, 0, w, h)
    const d = img.data
    for (let i = 0; i < d.length; i += 4) {
      const excess = (s[i] - b[i]) / 255      // >0 only where locally shadowed
      if (excess > 0) {
        const mul = 1 - reliefStrength * excess
        d[i] *= mul; d[i + 1] *= mul; d[i + 2] *= mul
      }
    }
    ctx.putImageData(img, 0, 0)
  }
  // Engraved letter edges, after relief and before the watermark — same reason: the
  // watermark and corner labels must stay clean.
  // The DECISION was taken in shoot(), where it can also be reported. By the time we are here
  // a non-zero outline width means "draw it" and nothing is re-derived.
  if (edgeMask && edgeStrength > 0 && edgeOutlinePx > 0) {
    letterEdges(ctx, { edgeMask, edgeStrength, outlinePx: edgeOutlinePx, w, h })
  }
  // tiled diagonal watermark (skipped for the image compositor, which adds its own)
  if (watermark) {
    ctx.save()
    ctx.globalAlpha = lightBg ? 0.10 : 0.16
    ctx.fillStyle = lightBg ? '#8a9197' : '#cfd4d8'
    ctx.font = `${Math.round(w * 0.05)}px -apple-system, sans-serif`
    ctx.textAlign = 'left'; ctx.textBaseline = 'middle'
    ctx.translate(w / 2, h / 2); ctx.rotate(-25 * Math.PI / 180); ctx.translate(-w / 2, -h / 2)
    const stepX = w * 0.34, stepY = h * 0.135
    for (let y = -h * 0.5; y < h * 1.5; y += stepY) {
      for (let x = -w * 0.5; x < w * 1.5; x += stepX) ctx.fillText('Osteo3d ®', x, y)
    }
    ctx.restore()
  }
  // top-right corner: big abbreviation (A/P/LL/RL/S/I/ISO) + smaller full name.
  // Tucked hard into the corner (viewFit fills the frame, so the label sits over the
  // mesh); a contrast halo keeps it legible over bone without a solid backing box.
  if (cornerLabel) {
    ctx.save()
    ctx.textAlign = 'right'; ctx.textBaseline = 'top'
    ctx.shadowColor = lightBg ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)'
    ctx.shadowBlur = w * 0.011
    ctx.fillStyle = lightBg ? '#0d2e3e' : '#ffffff'
    const rx = w - w * 0.012, ty = h * 0.012
    ctx.font = `bold ${Math.round(w * 0.072)}px -apple-system, sans-serif`
    ctx.fillText(cornerLabel, rx, ty)
    if (subLabel) {
      ctx.font = `${Math.round(w * 0.026)}px -apple-system, sans-serif`
      ctx.fillText(subLabel, rx, ty + w * 0.082)
    }
    ctx.restore()
  }
  return off.toDataURL('image/png').split(',')[1]
}

function groupColor(colors, name) {
  if (!name || !colors) return null
  // EXACT match only. The old endsWith/includes fallback mis-assigned colours on
  // ZBrush's numeric names — "Group1" matches "Group10", "Group15825", … — so a
  // colour could land on the wrong anatomical part. On a surgical model that is a
  // safety problem, not a cosmetic one: colour implies meaning to the surgeon.
  return colors[name] || null
}

/**
 * Find the engraved identification label plate among the loaded meshes.
 *
 * WHY THIS EXISTS. Under a matcap the label can never render. MeshMatcapMaterial is
 * unlit and shades purely from the view-space normal, so a raised letter's TOP face is
 * normal-identical to the plate around it and comes out the same colour; the letter side
 * walls do differ but project to ~zero width when the plate is viewed face-on — exactly
 * how the P view sees it. Measured on the real case, plate isolated and filling the
 * frame: std-dev 5.4 under the matcap (blank) vs 11.1 with real cast shadows, where the
 * text is crisply legible. Ruled out by the same experiment: crease angle (identical with
 * smoothNormals off), backface culling (identical — and 4228 of 4228 lettered faces are
 * already wound toward the camera), and GTAO at every radius from 0.005 to 0.15
 * (identical; AO cannot resolve a 0.43 mm step).
 *
 * So the label needs a LIT material and real cast shadows. Because matcap materials
 * ignore lights and shadows entirely, the lighting added for the label CANNOT alter the
 * bone — the approved look is preserved by construction, not by tuning.
 *
 * Identified by SHAPE, not by name: ZBrush polygroup names (`Group15825`) are per-export
 * and carry no meaning. A label plate is a thin slab, dense with faces for its size
 * (that is the engraving), and a small fraction of the model. Returns null when nothing
 * matches, in which case rendering is byte-for-byte what it was before.
 */
/**
 * Face-on gain of the label's ambient+key rig, MEASURED from a render, not guessed:
 * a flat plate facing the camera comes out this many times its material colour. Started
 * at 1.30 by assumption; the render said the plate landed at 0.77x of target, so the
 * true gain is ~1.0 — the ambient+key rig happens to be unity for a face-on surface. Dividing
 * by it makes the plate land ON the matcap's face-on tone instead of above it.
 *
 * Only the LEVEL is fitted; the HUE is derived from the matcap (see labelSurfaceColor),
 * which is the part that was wrong — a hard-coded RGB could not track the matcap's own
 * warmth and left the plate 38 levels too blue.
 */
export const LABEL_RIG_GAIN = 0.66

/**
 * Colour for the lit label plate so that, once the rig has lit it, it renders at the tone
 * the matcap would have given that same flat, camera-facing surface.
 *
 *   rendered ≈ colour × LABEL_RIG_GAIN        (flat, facing the camera)
 *   wanted   = tint × matcapFaceOn            (what the matcap does to such a surface)
 *   ⇒ colour = tint × matcapFaceOn / LABEL_RIG_GAIN
 *
 * `tint` is the group colour every bone group also receives, so the plate inherits the
 * SAME warmth chain as the bone (tint × matcap) instead of only half of it.
 * Falls back to the plain tint when the matcap carries no sample.
 */
export function labelSurfaceColor(THREE, tintHex, matcapTex) {
  const c = new THREE.Color(tintHex || '#ffffff')
  const f = matcapTex?.userData?.faceOnRGB
  if (!f) return c
  // `faceOnRGB` are sRGB bytes read off the matcap canvas, and `new THREE.Color(r,g,b)`
  // sets components in the LINEAR working space — feeding one to the other silently
  // desaturates. Measured: a colour at B/R 0.79 rendered at 0.906, and
  // sRGB(0.504)/sRGB(0.636) = 0.908 accounts for it exactly. `c` came from a hex string,
  // so three has already linearised it; multiply in linear, then hand the result back as
  // sRGB so it is converted once and correctly.
  const toSRGB = (v) => (v <= 0.0031308 ? v * 12.92 : 1.055 * Math.pow(v, 1 / 2.4) - 0.055)
  const lin = (v) => (v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4))
  const out = new THREE.Color()
  out.setRGB(
    Math.min(1, toSRGB((c.r * lin(f[0])) / LABEL_RIG_GAIN)),
    Math.min(1, toSRGB((c.g * lin(f[1])) / LABEL_RIG_GAIN)),
    Math.min(1, toSRGB((c.b * lin(f[2])) / LABEL_RIG_GAIN)),
    THREE.SRGBColorSpace,
  )
  return out
}

/**
 * Ambient and key intensity for the label rig, as a function of relief strength.
 *
 * WHAT ACTUALLY MAKES AN ENGRAVED STAMP FAINT. The first marked-stamp render read, but
 * weakly, and the two obvious suspects were both WRONG — measured on the real
 * O3D-2026-08-10-0002 export, Posterior view, mean 5x5 local range inside the stamp's
 * projected box (464x144 px), against the same view with the stamp left on the matcap:
 *
 *   flat, no relief                          8.92
 *   shipping rig                            16.82   1.89x
 *   shadow normalBias 0.004 -> 0.0005       16.82   no effect whatsoever
 *   shadow map 2048 -> 4096, radius 2 -> 1  16.82   no effect whatsoever
 *   key elevation 45 -> 72 degrees          18.04   negligible, and see below
 *   AMBIENT FLOOR 0.403 -> 0.20             20.66   this is the entire lever
 *
 * The bias and map-size changes are below the GTAO noise floor — 0.013 mean per channel
 * against 0.03 — i.e. indistinguishable from re-rendering the same frame twice. Raising
 * the key elevation does help slightly but is rejected on a different ground: the stamp
 * is engraved into the CURVED occiput, so a grazing key drops the lit plate from 220 to
 * 199 while surrounding bone stays put, and the stamp starts reading as a dark patch
 * stuck onto the skull rather than as engraved bone.
 *
 * So the only honest control is how dark a fully self-shadowed pixel goes, which is the
 * ambient:key ratio. `floor` is ambient as a fraction of the face-on total.
 *
 * THE FACE-ON TOTAL IS HELD CONSTANT, which is the whole point. Brightness is a free
 * parameter here and contrast is not (see the rig itself below): keeping
 * `ambient + key x cos(theta)` fixed keeps the lit plate ON the bone's tone while only the
 * letter shadows deepen. Measured across the full sweep, the lit plate held at p95 219-220
 * from floor 0.403 all the way to 0.05, while the letters went 161.5 -> 130.1. That is
 * also what keeps LABEL_RIG_GAIN valid — it is fitted to that total.
 *
 * `strength` 0 returns the shipped literals unchanged, so an unmarked file renders exactly
 * as it does today and the Surgical Suite Engine match is untouched. That identity is a
 * load-bearing property, not an implementation detail, so it is an explicit early return
 * rather than something the algebra is trusted to reproduce bit-for-bit.
 */
export const LABEL_RIG = {
  // Measured against the reference case; see labelSurfaceColor's LABEL_RIG_GAIN, which is
  // fitted to the face-on total these two produce at the shipped light angles.
  ambient0: 0.953,
  key0: 2.305,
  // Ambient fraction at strength 1 — the strongest point measured. Deliberately beyond the
  // useful band so the operator's default sits INSIDE the slider's travel rather than at its
  // end: the printed page reads from about floor 0.14 (strength 0.75, the marked default),
  // and this leaves headroom above that for an unusually shallow engraving.
  floorMax: 0.05,
  // Hard ceiling on how far the rebalance may push the key, as a multiple of key0.
  //
  // Rebalancing divides by the key's effective cosine, so a light the operator has dragged
  // toward grazing sends the key up without limit — at azimuth -85 / elevation 80, both
  // reachable on the panel's own sliders, the arithmetic asks for a key over 20x. The face-on
  // total would still be nominally correct, but this stamp is engraved into the CURVED
  // occiput, so any facet tilted toward the key saturates and the plate blows to white with
  // the lettering gone, on a surgical document, with relief.applied still reporting true.
  // Past the cap the shipped literals are used instead: a dim plate at an extreme light
  // angle, which is exactly what the renderer does at that angle today.
  keyCap: 4,
}

export function labelRigLights(
  strength = 0, { azimuth = -30, elevation = 45, intensity = 1 } = {},
) {
  const { ambient0, key0, floorMax, keyCap } = LABEL_RIG
  const s = clamp01(strength)
  if (s === 0) return { ambient: ambient0, key: key0 }
  // The key's cosine against a camera-facing plate, from the SAME expression shoot() uses to
  // place the light (its +Z component), times the light intensity the renderer will apply.
  //
  // `intensity` IS PART OF THE BALANCE, and leaving it out was a defect caught in review. The
  // renderer builds the ambient at rig.ambient but the key at rig.key * lightIntensity, so the
  // rendered total is ambient + key * intensity * cos. Balancing without the intensity term is
  // only correct at 1.0 — and no report path uses 1.0. REPORT_LOOK and the panel both ship
  // 1.2, and the panel hides the intensity slider in matcap mode, so 1.2 is the ONLY value a
  // matcap report ever renders at. Measured on the reference case at 1.2: the lit plate went
  // 229.1 -> 231.3 as strength rose, i.e. the stamp was drifting BRIGHTER than the bone around
  // it — the mirror image of the patch this design exists to avoid.
  const cos = Math.cos(azimuth * Math.PI / 180) * Math.cos(elevation * Math.PI / 180)
  const k = cos * intensity
  if (!(k > 0)) return { ambient: ambient0, key: key0 }   // key edge-on or behind: nothing to balance
  const total = ambient0 + key0 * k
  const floor0 = ambient0 / total
  const ambient = total * (floor0 + (floorMax - floor0) * s)
  const key = (total - ambient) / k
  if (!(key <= key0 * keyCap)) return { ambient: ambient0, key: key0 }
  return { ambient, key }
}

/**
 * Should the WHOLE-FRAME self-shadow pass run?
 *
 * This is the fallback for engraving that cannot be isolated as a mesh. It shadows the
 * entire model, not one plate, and it is the pass the team reported as "too much shadow"
 * on 2026-08-14 — measured against it being off: mean 1.90/255, worst pixel 117, 8.0% of
 * pixels changed, against a GTAO noise floor of 0.03. It is off by default for that reason.
 *
 * Extracted rather than left inline BECAUSE OF `stampGroup`, which is a guard and not a
 * tidy-up. Marking a stamp now switches relief strength on by itself (STAMP_RELIEF_MARKED
 * in ReportLookPanel), and strength is what arms this pass — so a marked group that fails
 * to resolve, after a rename on re-export or a typo or simply the wrong file, would have
 * quietly traded a one-mesh light for a self-shadow pass across the whole skull. The
 * operator marking a stamp must not be able to reach this pass at all. When the name does
 * not resolve they get the warning banner instead; that is what reliefStatus is for.
 *
 * Inline, that condition was five `&&` terms in a render function no unit test can reach,
 * which is exactly how the earlier version of this feature shipped with zero coverage.
 */
export function wholeFrameRelief({
  useMatcap, reliefShadow, reliefStrength, labelFound, stampGroup,
}) {
  if (!useMatcap) return false        // the lit paths already shade relief correctly
  if (!reliefShadow) return false
  if (labelFound) return false        // a real plate was isolated; the Lambert path wins
  if (stampGroup) return false        // marked but unresolved — warn, never shadow the model
  return clamp01(reliefStrength) > 0
}

/**
 * Did the engraved-stamp relief actually engage, and if not, why?
 *
 * WHY THIS IS REPORTED AT ALL. When `labelRelief` finds no plate it does nothing, and the
 * resulting sheet is indistinguishable from a correct one — the stamp is simply absent from
 * the face-on view, which is the ONE view the lettering is authored to face. That is how
 * O3D-2026-08-10-0002 went out with a blank Posterior stamp: the render succeeded, every
 * check passed, and nothing anywhere said the feature had not run.
 *
 * `single-mesh` is the structural case, not a tuning miss. STLLoader returns exactly ONE
 * mesh (see loadMeshes), and detectLabelMesh needs at least two so it has something to
 * measure the candidate against — so `labelRelief` can NEVER engage on a .stl upload,
 * whatever the file contains. An OBJ carrying the stamp as its own polygroup works.
 */
export function reliefStatus({
  labelRelief, useMatcap, found, meshCount, shadowFallback = false, stampReason = null,
}) {
  // `applied` answers ONE question: will the engraving read? Not "did a particular code path
  // run". Three different mechanisms all deliver it — an isolated plate lit with Lambert, the
  // self-shadow pass, and the Realistic rig, which lights and shadows everything anyway.
  //
  // Lit mode has to be in that list. Without it, flipping Render style to Realistic produced
  // requested=true, applied=false and fired the banner on a render where the text reads
  // perfectly. A warning shown on correct output is worse than no warning: it teaches the
  // operator to dismiss the one case that matters.
  const applied = Boolean(found) || Boolean(shadowFallback) || !useMatcap
  return {
    requested: Boolean(labelRelief),
    applied,
    // A MARKED group that did not resolve outranks the generic answers below, and it has to.
    // Without it the operator marks a group, the name is absent from the file, and the banner
    // says "no engraved plate was found in this file and the self-shadow relief pass is
    // switched off" — which reads as "marking changed nothing", is the same text they saw
    // BEFORE marking, and is now false on both halves: a plate was named, and relief is on at
    // STAMP_RELIEF_MARKED and was deliberately suppressed. They conclude the control does not
    // work and send the blank Posterior. resolveStampMeshes already computes exactly why; this
    // is that answer reaching the person who can act on it.
    reason: !labelRelief ? 'not-requested'
      : !useMatcap ? 'lit-mode'          // the lit paths already shade relief correctly
        : found ? 'applied'
          : stampReason === 'not-found' ? 'stamp-not-found'
            : stampReason === 'too-large' ? 'stamp-too-large'
              : shadowFallback ? 'shadow-relief'
                : meshCount < 2 ? 'single-mesh'
                  : 'no-candidate',      // >= 2 meshes, none passed the shape gates
  }
}

/**
 * Resolve the group the OPERATOR marked as the engraved stamp.
 *
 * Extracted and exported so it can be unit-tested: the first version of this lived
 * inline in renderReport as `meshes.find(...) || detectLabelMesh(meshes)`, and a review
 * showed that breaking the name match entirely left every test green.
 *
 * Three things the inline version got wrong, each pinned by a test now:
 *  · ALL meshes with the name, not the first. OBJLoader starts a new object per `g`/`o`
 *    line and copies the group name onto each, so one polygroup can arrive as several
 *    identically-named meshes — the fixtures in this module's test file record three
 *    called `Group33290`. Lighting only the first leaves the rest of the stamp unlit.
 *  · NEVER fall back to the heuristic when a marked name does not resolve. Falling back
 *    made `found` true, so the "engraving may not read" banner stayed silent and the
 *    operator was never told their choice had been discarded.
 *  · The 25% guard still applies. The named path bypassed it, so one mis-click on a
 *    truncated row could light an anatomy half in cream Lambert on a surgical report.
 *
 * A FABRICATED PART IS NEVER A STAMP. The label path renders its mesh with
 * labelSurfaceColor(), which is derived from the BONE matcap and takes no account of
 * `partNeutral` — so a splint or guide accepted here would come out cream instead of the
 * neutral white appliances are supposed to get, which is the exact regression that was
 * caught and reverted twice while this feature was being built (see detectLabelMesh's
 * tests). Nothing wires a stamp picker into the parts path today, but the `partName`
 * match below reaches it, so the refusal belongs here rather than in a future caller's
 * memory. An engraved case ID is cut into anatomy; if it were ever cut into an appliance
 * this refusal is the thing to revisit, deliberately.
 *
 * @returns {{meshes: THREE.Mesh[], reason: 'not-marked'|'not-found'|'too-large'|'non-anatomy'|'marked'}}
 */
export function resolveStampMeshes(meshes, stampGroup, { maxFraction = 0.25 } = {}) {
  if (!stampGroup) return { meshes: [], reason: 'not-marked' }
  const named = (meshes ?? []).filter(
    (m) => m.name === stampGroup || m.userData?.partName === stampGroup,
  )
  if (!named.length) return { meshes: [], reason: 'not-found' }
  if (named.some((m) => m.userData?.partNeutral)) return { meshes: [], reason: 'non-anatomy' }
  const count = (m) => m.geometry?.attributes?.position?.count || 0
  const total = (meshes ?? []).reduce((n, m) => n + count(m), 0)
  const picked = named.reduce((n, m) => n + count(m), 0)
  if (total > 0 && picked > total * maxFraction) return { meshes: [], reason: 'too-large' }
  return { meshes: named, reason: 'marked' }
}

export function detectLabelMesh(meshes) {
  if (meshes.length < 2) return null
  const count = (m) => m.geometry?.attributes?.position?.count || 0
  const total = meshes.reduce((n, m) => n + count(m), 0)
  let best = null
  for (const m of meshes) {
    const n = count(m)
    if (n < 1500) continue              // specks and stray polygroups
    if (n > total * 0.25) continue      // the anatomy itself
    m.geometry.computeBoundingBox?.()
    const b = m.geometry.boundingBox
    if (!b) continue
    const d = [b.max.x - b.min.x, b.max.y - b.min.y, b.max.z - b.min.z].sort((p, q) => p - q)
    if (!(d[2] > 0) || !(d[1] > 0)) continue
    // Thin — but thinness alone is not enough. OBJLoader splits a ZBrush export into
    // dozens of meshes and bone slivers are thinner still: on the reference case a
    // 2,265-vertex shard measures 0.015 against the label's 0.066, so "thinnest wins"
    // picks the shard.
    //
    if (d[0] / d[2] > 0.10) continue
    // VERTEX DENSITY is the property that actually identifies a label: it is dense
    // *because* it is engraved. Reference case — label 92,778 verts over 64.9 x 21.4 mm
    // = 67 verts/mm², the bone shard ~4. A blank plate would fall below the floor and
    // be left on the matcap, which is correct: there would be nothing to reveal.
    const density = n / (d[1] * d[2])
    if (density < 20) continue
    if (!best || density > best.density) best = { mesh: m, density }
  }
  return best?.mesh || null
}

/**
 * Render the SSE report views + compute metrics.
 * @param {ArrayBuffer|Uint8Array} bytes
 * @param {object} opts { filename, background:'white'|'black', boneColor, shadingT(0-1),
 *                        roughness(0.3-1), res=1600, colors?, views? }
 * @returns {Promise<{ views:{code:b64}, ortho:string[], iso:string, labels, metrics }>}
 */
/**
 * THE report look — one definition, three consumers.
 *
 * These are the settings the team tuned over many iterations for the STL/OBJ
 * report: the skeleton matcap, the bone tone, GTAO crevice shading and the key
 * light. They lived only inside PreviewPage, so when the Case Report Builder
 * came along it grew a SECOND, hand-written option set that quietly diverged —
 * it never passed `matcapUrl` at all, so the entire look was absent, and with
 * `boneColor` unset the default tone was read as an explicit tint and darkened
 * everything by ~18%. The bone came out flat olive-taupe instead of ivory.
 *
 * Two hand-maintained option sets is the same failure `CT_FIELDS` had. Same
 * remedy: one source, one answer.
 *
 * Consumers:
 *   · PreviewPage.jsx      — spreads this, then overrides from its UI state
 *                            (bone-tone picker, matcap toggle, the sliders)
 *   · caseReport/renderScenes.js — takes it as-is, plus usePolypaint
 *   · tools/render-hash/hash.mjs — hashes it, so a change here is visible
 *
 * `matcapUrl` is deliberately spelled out rather than made conditional: a caller
 * that wants the unlit-matcap look off passes `matcapUrl: null` explicitly.
 */
export const REPORT_LOOK = {
  background: 'white',
  boneColor: '#CFC1A7',
  shadingT: 0.5,
  roughness: 0.82,
  matcapUrl: '/matcaps/skeleton.png',
  matcapBrightness: 1.1,
  shadows: true,               // GTAO crevice ambient occlusion — the BPR look
  aoIntensity: 1.3,
  castShadows: false,          // matcap materials ignore lights; see PreviewPage
  lightAzimuth: -30,
  lightElevation: 45,
  lightIntensity: 1.2,
  cull: true,
  viewFit: true,
  flipAP: true,
  // Report-path quality (opt-in; the packaging label deliberately does NOT get these)
  smoothNormals: true,
  cameraRelativeKey: true,
  // An engraved case label cannot render under a matcap (unlit → a raised
  // letter's top face is normal-identical to the plate). This lights that ONE
  // mesh so the text reads; matcap materials ignore lights, so bone is untouched.
  labelRelief: true,
  // …and the fallback for when there is no plate to isolate, which is the normal case on
  // the team's exports. It makes a blank occiput read as "Osteo3d® / O3D202608100002".
  //
  // 🔴 OFF BY DEFAULT since 2026-08-14, on the team's report of "too much shadow".
  // This pass exists ONLY in the portal — the Surgical Suite Engine has no relief code
  // at all — so with it on, the two apps could not match however the sliders were set,
  // and shared/report-look.json cannot see the difference because relief is not in it.
  //
  // The cost was always known and was under-weighted: the note here used to record
  // "mean change to bone elsewhere is 2.07/255" as acceptable. MEASURED again on the
  // delivered case's native skull, on -> off is mean 1.90/255, worst pixel 117, and
  // 8.0% of pixels changed — against a GTAO noise floor of 0.03. That is not a rounding
  // error on a surgical document, it is the shadow the team was objecting to.
  //
  // The capability is not gone: the Report look panel now carries a `Stamp relief`
  // slider, 0 = off, which an operator raises on the one case where an engraved ID will
  // not read face-on. `reliefStrength` below is where that slider starts from, not a
  // value that applies while reliefShadow is false.
  reliefShadow: false,
  reliefStrength: 0.55,
}

export async function renderReport(bytes, {
  filename = 'mesh.stl', background = 'white', boneColor = '#CFC1A7',
  shadingT = 0.5, roughness = 0.82, res = 1600, colors = null, views = null,
  cornerLabels = true, flipAP = false, lpsFrame = false, matcapUrl = null, matcapBrightness = 1.3, cull = false, viewFit = false, watermark = true,
  shadows = false, aoIntensity = 1.0, aoRadius = 0.15, perspective = false,
  castShadows = false, lightAzimuth = -30, lightElevation = 45, lightIntensity = 1.0,
  // Report-path quality opts. DEFAULT OFF so the Packaging Illustration
  // (PackagingPage.jsx → renderReport without these) stays byte-identical — it is
  // a printed label that must not shift. Measured: enabling either of these changes
  // every packaging view's PNG hash, so they are opt-in, not global.
  smoothNormals = false, cameraRelativeKey = false,
  // Extra cameras to render in addition to the six orthographic views and ISO.
  // Each entry is a built-in code (see EXTRA_VIEW_DEFS) or a caller-supplied
  // `{ code, dir, up }`. Empty by default, so no existing caller changes.
  extraViews = [],
  // Engraved-label rendering (see detectLabelMesh). Opt-in so the Packaging Illustration
  // — which renders the same meshes and must stay byte-identical — is unaffected.
  // The plate's colour is DERIVED from the matcap (labelSurfaceColor), not configured —
  // a hand-tuned constant lived here and could not track the matcap's own warmth, which
  // is what made the plate render pale and blue against cream bone.
  labelRelief = false,
  // The NAME of the group/part the operator has marked as the engraved stamp.
  //
  // WHY THIS EXISTS RATHER THAN A BETTER GUESS. detectLabelMesh identifies a flat,
  // dense plate and that is genuinely all it can do. A case ID engraved into the
  // CURVED occiput measures thin 0.281 — ABOVE a 60x25x8 mm cutting guide's 0.133 — so
  // no thinness threshold separates a stamp from an appliance, and on the reference
  // file the anatomy sits at 18.6-25.2 verts/mm² against the stamp's 26.1. Two attempts
  // to widen the gates were each reviewed, reproduced and reverted: one lit a slice of
  // skull on a large model, the other picked a marking guide and would have rendered it
  // cream instead of the neutral white appliances get. When the shape cannot tell you,
  // the operator can. Explicit beats a heuristic that is wrong on a surgical document.
  //
  // When set, detectLabelMesh is not consulted at all.
  stampGroup = null,
  // Engraving that CANNOT be isolated as a mesh — the normal case, not the exception.
  // Measured on the real O3D-2026-08-10-0002 export ("Merged_Back skull images.OBJ"): the
  // "Osteo3d / O3D2..." lettering is embossed straight into the occipital bone, and the file's
  // three polygroups were two anatomy halves (50% and 43% of the model) plus a 68x71x81 mm
  // part at thin=0.841 — nothing a plate detector could pick out, so labelRelief no-opped and
  // the text died face-on in P while surviving obliquely in LL.
  //
  // ⚠️ The team RE-EXPORTED that file on 2026-08-14 with the stamp as its own polygroup
  // (Group47044, 32,973 verts, 16.7 x 21.2 x 59.4 mm, 26.1 verts/mm²). detectLabelMesh
  // still does NOT find it — thin 0.281 against the 0.10 gate — and widening that gate was
  // tried twice and reverted twice, because a curved stamp is measurably LESS plate-like
  // than a cutting guide. The operator marks it instead: see `stampGroup`.
  //
  // So the relief cue comes from the RENDER, not from isolating geometry: a second pass whose
  // only output is the self-shadow term, multiplied onto the untouched matcap frame. A pixel
  // that is not self-shadowed multiplies by exactly 1, so smooth bone cannot shift — the
  // approved look is preserved by construction, the same argument that made labelRelief safe.
  // Applied ONLY when no label plate was found; where one is found the proven Lambert path wins.
  reliefShadow = false,
  // How dark a fully self-shadowed pixel goes. 1.0 = black, 0 = no-op.
  reliefStrength = 0.55,
  // Multi-file scene. When given, `bytes` is ignored and the scene is composed
  // from these parts, each with its own colour. Additive: nothing that omits
  // `parts` behaves differently. See loadScene().
  parts = null,
  // Use the ZBrush polypaint carried in the OBJ's `#MRGB` lines instead of the
  // operator's group colours. OFF by default so nothing changes under a team
  // that already picks colours by hand — see loadMeshes().
  usePolypaint = false,
  // Render parts flagged `neutral` against a desaturated lit-sphere so splints and
  // guides read as white resin instead of cream bone. See deriveNeutralMatcap.
  //
  // OFF by default, and it only ever applies to parts the caller has flagged — so
  // the Packaging Illustration and the STL/OBJ-to-PDF path, neither of which passes
  // `parts` with a `neutral` flag, cannot move. Guarded by the `scene*` configs in
  // tools/render-hash.
  neutralNonAnatomy = false,
  // Force the shared orthographic scale instead of deriving it from this scene.
  //
  // This is what makes a Native | Post-Plan pair comparable. Sizing each scene
  // to its own extent means the two columns are at different scales, and a
  // surgeon reading a planning document has no reason to suspect that. The same
  // class of defect already shipped once here: per-view framing rendered the
  // laterals 1.66x oversized against the frontal views.
  //
  // Two-pass usage: render each scene in a fit group once, take the largest
  // returned `fitScale`, then re-render them all with that value.
  fitScaleOverride = null,
} = {}) {
  const { THREE, STLLoader, OBJLoader, GLTFLoader, DRACOLoader, toCreasedNormals } = await three()
  const mod = { THREE, STLLoader, OBJLoader, GLTFLoader, DRACOLoader }
  let meshes
  if (parts?.length) {
    meshes = await loadScene(mod, parts, { usePolypaint })
  } else {
    const buf = bytes instanceof ArrayBuffer ? bytes : bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength)
    meshes = await loadMeshes(mod, buf, extOf(filename), { usePolypaint })
  }
  if (!meshes.length) throw new Error('No mesh geometry found in file')

  const metrics = computeMetrics(meshes, THREE)

  // Shading lerp
  const t = clamp01(shadingT)
  const lerp = (s, hh) => s + t * (hh - s)
  const sh = {
    ambient: lerp(SOFT.ambient, HARD.ambient), keyMul: lerp(SOFT.keyMul, HARD.keyMul),
    hemi: lerp(SOFT.hemi, HARD.hemi), exposure: lerp(SOFT.exposure, HARD.exposure),
  }
  // background: 'white' | 'black' | any '#rrggbb' (image compositor passes a charcoal
  // so the renders sit seamlessly on the canvas). Non-white → treat as dark.
  const dark = background !== 'white'
  const bgColor = background === 'white' ? 0xffffff
    : background === 'black' ? 0x000000
    : (parseInt(String(background).replace('#', ''), 16) || 0x000000)

  // Matcap (ZBrush "matcap skeleton" look). Per-group OBJ colours USED to disable it
  // entirely (`!!matcapTex && !colors`), so any multi-group OBJ — i.e. every ZBrush
  // export with polygroups — silently rendered with the weak lit fallback instead and
  // could never look like the reference. MeshMatcapMaterial has its own `color`, which
  // multiplies the matcap, so a group tint and the matcap shading now coexist.
  const matcapTex = matcapUrl ? await loadMatcap(THREE, matcapUrl) : null
  const useMatcap = !!matcapTex
  // Built once per render, and only when something actually asks for it.
  const neutralTex = (useMatcap && neutralNonAnatomy) ? deriveNeutralMatcap(THREE, matcapTex) : null

  // cull (backface culling / FrontSide) stops the posterior camera seeing THROUGH a
  // hollow mesh to the inside of the front. DoubleSide is the safe default (no holes
  // on inconsistent-winding meshes); STL/OBJ-to-PDF opts into culling.
  const side = cull ? THREE.FrontSide : THREE.DoubleSide
  const scene = new THREE.Scene()
  const group = new THREE.Group()
  // Engraved label plate: matcap cannot show its relief (see detectLabelMesh), so this
  // ONE mesh is rendered lit with real cast shadows while the bone keeps the matcap.
  // Only relevant in matcap mode — the lit paths already shade relief correctly.
  const stamp = resolveStampMeshes(meshes, stampGroup)
  // A Set, because one polygroup can arrive as several meshes sharing a name.
  const labelSet = new Set()
  if (useMatcap && labelRelief) {
    if (stamp.meshes.length) {
      for (const m of stamp.meshes) labelSet.add(m)
    } else if (!stampGroup) {
      // Only guess when nothing was marked. A marked group that did not resolve must
      // NOT silently fall through to the heuristic — the operator has to be told.
      const guessed = detectLabelMesh(meshes)
      if (guessed) labelSet.add(guessed)
    }
  }
  const labelMesh = labelSet.size ? [...labelSet][0] : null
  const useReliefShadow = wholeFrameRelief({
    useMatcap, reliefShadow, reliefStrength, labelFound: Boolean(labelMesh), stampGroup,
  })
  const relief = reliefStatus({
    labelRelief, useMatcap, found: Boolean(labelMesh), meshCount: meshes.length,
    stampReason: stamp.reason,
    shadowFallback: useReliefShadow,
  })
  for (const m of meshes) {
    // OBJLoader de-indexes, and a ZBrush OBJ export carries no `vn` lines, so
    // computeVertexNormals() on the raw geometry yields FLAT per-face normals —
    // the triangle faceting the reference doesn't have.
    //
    // CREASED, not smooth. Plain weld-then-average also smooths across genuine
    // sharp edges, which ERASES ~1 mm engraved text (verified by rendering: the
    // plate came out uniformly flat and unreadable). toCreasedNormals averages
    // only across faces meeting below the crease angle, so the surface smooths
    // while the engraving keeps its hard edges.
    //
    // (An earlier note here claimed this was "the only way the text can read under a
    // matcap". It is not — nothing is. A matcap is unlit, so a raised letter's top face
    // is normal-identical to the plate and cannot differ in colour whatever the normals
    // do. Measured: smoothNormals on/off gives a byte-identical blank plate. That is
    // what detectLabelMesh exists to solve.)
    if (smoothNormals && toCreasedNormals) {
      try {
        const cn = toCreasedNormals(m.geometry, CREASE_ANGLE)
        if (cn?.attributes?.normal) { m.geometry.dispose?.(); m.geometry = cn }
        else m.geometry.computeVertexNormals?.()
      } catch { m.geometry.computeVertexNormals?.() }
    } else {
      m.geometry.computeVertexNormals?.()
    }
    // A scene part's colour wins over the OBJ-group lookup: it was assigned to
    // this FILE by the classifier or the operator, which is more specific than
    // any group name inside it.
    const grp = m.userData?.partColor ?? groupColor(colors, m.name) // hex string or null
    // A group left on the DEFAULT bone tone is not a colour choice — it is "no choice".
    // In matcap mode a group colour MULTIPLIES the matcap, so treating the default as a
    // tint darkened the whole model the moment a file had more than one group: measured
    // 186,174,143 (matcap as authored) -> 160,141,101 on the team's merged two-OBJ export,
    // an ~18% drop nobody asked for. Only a colour the operator actually changed tints.
    const tint = grp && grp.toLowerCase() !== String(boneColor).toLowerCase() ? grp : null
    const isLabel = labelSet.has(m)
    // Polypaint multiplies the matcap exactly as a group tint does, and ZBrush's
    // unpainted white multiplies to a no-op — so painted and unpainted regions of
    // the SAME mesh both come out right with no per-region material.
    //
    // 🔴 But the two are MUTUALLY EXCLUSIVE, never combined. Both multiply the
    // matcap, so leaving both applied renders tint × paint: a group painted
    // #91DD6A that the operator then tinted #88C9FC came out #408F46 — they
    // picked blue and got a darker green. An explicit operator colour REPLACES
    // the file's paint for that group; that is what "override" has to mean.
    //
    // Guarded by the `paintTinted` config in tools/render-hash, which is the
    // config whose absence let this ship: `paint` has no tint and every other
    // config has no paint, so the defect changed nothing they measured.
    const painted = (m.geometry?.hasAttribute?.('color') ?? false) && !tint
    // A fabricated part takes the neutral lit-sphere; anatomy keeps the bone one.
    // Falls back to the bone matcap if the derivation failed, so a canvas error
    // costs the whiteness and not the render.
    const meshMatcap = (neutralTex && m.userData?.partNeutral) ? neutralTex : matcapTex
    m.material = (useMatcap && !isLabel)
      ? new THREE.MeshMatcapMaterial({
        matcap: meshMatcap, side, vertexColors: painted,
        // Untinted stays pure white: the matcap exactly as authored.
        color: new THREE.Color(tint || 0xffffff),
      })
      : (isLabel && useMatcap
        // LAMBERT, not Standard, for the label. A matcap cannot show face-on relief, so
        // this one mesh is lit — but MeshStandardMaterial adds a white specular lobe, and
        // a flat plate square-on to a camera-relative key catches it fully. Measured: fed
        // a colour at B/R 0.79 it rendered at 0.906, i.e. visibly desaturated toward grey
        // next to warm bone. Lambert is pure diffuse, so the hue survives and still takes
        // the cast shadows that make the letters readable.
        ? new THREE.MeshLambertMaterial({
          color: labelSurfaceColor(THREE, tint, matcapTex), side,
        })
        : new THREE.MeshStandardMaterial({
          color: new THREE.Color(painted ? 0xffffff : (grp || boneColor)),
          vertexColors: painted,
          roughness, metalness: 0.0, side, flatShading: false,
        }))
    // Self-shadowing: Realistic mode for everything; matcap mode for the label only,
    // which is the whole point — the letters cast onto the plate and become readable.
    // receiveShadow is also what makes three compile USE_SHADOWMAP into the mask material,
    // so it is required on every mesh for the relief pass, not just cosmetic.
    if (castShadows || isLabel || useReliefShadow) { m.castShadow = true; m.receiveShadow = true }
    group.add(m)
  }
  scene.add(group)

  const box = new THREE.Box3().setFromObject(group)
  const center = box.getCenter(new THREE.Vector3())
  const radius = box.getBoundingSphere(new THREE.Sphere()).radius || 1
  // 8 AABB corners — for viewFit (fit each view to its projected 2D extent)
  const corners = []
  for (const px of [box.min.x, box.max.x]) for (const py of [box.min.y, box.max.y]) for (const pz of [box.min.z, box.max.z]) corners.push(new THREE.Vector3(px, py, pz))

  const canvas = document.createElement('canvas')
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, preserveDrawingBuffer: true, alpha: true })
  renderer.setSize(res, res, false)
  // Shadows path renders through EffectComposer, whose OutputPass tone-maps the WHOLE
  // frame — so a white bg × exposure(<1) would go grey. Render the bg TRANSPARENT there
  // and fill the true colour in 2D (stampView). Opaque otherwise (unchanged).
  const bgCss = `#${(bgColor >>> 0).toString(16).padStart(6, '0').slice(-6)}`
  renderer.setClearColor(bgColor, shadows ? 0 : 1)
  // Real shadow-mapping. Realistic lit mode uses it throughout; matcap mode enables it
  // solely for the label plate, whose engraving is invisible without it. Matcap meshes
  // neither cast nor receive here, so the bone is untouched either way.
  renderer.shadowMap.enabled = (castShadows && !useMatcap) || !!labelMesh || useReliefShadow
  renderer.shadowMap.type = THREE.PCFSoftShadowMap
  // Matcap encodes its own shading → no tone mapping / no lights. Otherwise the lit
  // MeshStandardMaterial rig: ambient + soft key/fill + hemisphere (shadingT tunes contrast).
  // Matcap: LinearToneMapping (pure exposure multiply, no ACES colour shift) so
  // matcapBrightness can brighten it without altering the ZBrush hue. Else ACES.
  renderer.toneMapping = useMatcap ? THREE.LinearToneMapping : THREE.ACESFilmicToneMapping
  renderer.toneMappingExposure = useMatcap ? matcapBrightness : sh.exposure
  // Realistic mode keeps a reference to the shadow-casting key light so shoot() can
  // reposition it per view (camera-relative → every view lit consistently).
  let keyLight = null, keyTarget = null
  if (!useMatcap && castShadows) {
    // Realistic (BPR): low ambient + hemi fill so the directional key + real shadows read.
    scene.add(new THREE.AmbientLight(0xffffff, 0.40))
    scene.add(new THREE.HemisphereLight(0xffffff, 0x555555, 0.35))
    keyLight = new THREE.DirectionalLight(0xffffff, 3.0 * lightIntensity)
    keyLight.castShadow = true
    keyLight.shadow.mapSize.set(2048, 2048)
    keyLight.shadow.radius = 4 // soft shadow edges (BPR look; PCFSoft blur kernel)
    const sc = keyLight.shadow.camera
    sc.left = -radius * 1.3; sc.right = radius * 1.3; sc.top = radius * 1.3; sc.bottom = -radius * 1.3
    sc.near = radius * 0.5; sc.far = radius * 6
    keyLight.shadow.bias = -0.0005
    keyLight.shadow.normalBias = radius * 0.02 // scale-dependent — kills acne on noisy CT meshes
    keyTarget = new THREE.Object3D(); scene.add(keyTarget)
    keyLight.target = keyTarget
    scene.add(keyLight)
  } else if (useMatcap && labelMesh) {
    // Matcap mode with an engraved label. These lights reach the label ONLY — matcap
    // materials ignore lighting entirely — so the bone renders exactly as it did before
    // this branch existed. Ambient carries the plate's base tone; the camera-relative
    // key rakes across the letters in every view so the relief casts and reads.
    // Ambient and key are scaled TOGETHER so the plate lands on the bone's measured tone
    // without altering the light-to-shadow ratio the letters depend on — brightness is a
    // free parameter here, contrast is not. Tuned against the reference case by measuring
    // both regions: plate 169,161,141 -> 215,204,171 vs bone 215,204,171.
    //
    // `reliefStrength` deepens the letters WITHOUT moving the plate: see labelRigLights,
    // which holds the face-on total fixed and only redistributes it between ambient and
    // key. At 0 this is the shipped 0.953 / 2.305 exactly.
    const rig = labelRigLights(reliefStrength, {
      azimuth: lightAzimuth, elevation: lightElevation, intensity: lightIntensity,
    })
    scene.add(new THREE.AmbientLight(0xffffff, rig.ambient))
    keyLight = new THREE.DirectionalLight(0xffffff, rig.key * lightIntensity)
    keyLight.castShadow = true
    keyLight.shadow.mapSize.set(2048, 2048)
    keyLight.shadow.radius = 2
    const sc = keyLight.shadow.camera
    sc.left = -radius * 1.3; sc.right = radius * 1.3; sc.top = radius * 1.3; sc.bottom = -radius * 1.3
    sc.near = radius * 0.5; sc.far = radius * 6
    keyLight.shadow.bias = -0.0005
    keyLight.shadow.normalBias = radius * 0.004 // small: the engraving is ~0.4 mm deep
    keyTarget = new THREE.Object3D(); scene.add(keyTarget)
    keyLight.target = keyTarget
    scene.add(keyLight)
  } else if (useReliefShadow) {
    // Engraving relief with NO plate to isolate. Only the shadow TERM is wanted, so no
    // ambient and no fill are added: the mask shader reads getShadowMask() and ignores
    // light colour and intensity entirely, and the matcap meshes ignore lights anyway.
    // This light exists purely to cast a shadow map that the embossed letters self-shadow into.
    keyLight = new THREE.DirectionalLight(0xffffff, 1.0)
    keyLight.castShadow = true
    keyLight.shadow.mapSize.set(4096, 4096)   // ~0.05 mm/texel over a 200 mm skull; the
    keyLight.shadow.radius = 1                // lettering is a sub-millimetre step
    const sc = keyLight.shadow.camera
    sc.left = -radius * 1.3; sc.right = radius * 1.3; sc.top = radius * 1.3; sc.bottom = -radius * 1.3
    sc.near = radius * 0.5; sc.far = radius * 6
    keyLight.shadow.bias = -0.0005
    // Small, like the label rig: a large normalBias pushes the sample off the ~0.4 mm relief
    // and erases exactly the signal being harvested. Too small and raw CT noise self-shadows.
    keyLight.shadow.normalBias = radius * 0.004
    keyTarget = new THREE.Object3D(); scene.add(keyTarget)
    keyLight.target = keyTarget
    scene.add(keyLight)
  } else if (!useMatcap) {
    // Flat lit fallback (no shadows) — the shadingT soft/hard rig.
    //
    // The key is CAMERA-RELATIVE (repositioned per view in shoot(), like the
    // Realistic path). It used to be world-fixed at (1, 1.4, 1): any view whose
    // surface faced away from that one direction received only ambient + hemi,
    // so shallow relief — e.g. ~1 mm engraved text on the posterior plate —
    // produced no gradient at all and was INVISIBLE, while the same text read
    // fine in LL where the fixed key happened to rake across it. GTAO cannot
    // compensate: its radius is boundingRadius × aoRadius ≈ 18 mm here.
    scene.add(new THREE.AmbientLight(0xffffff, sh.ambient))
    const key = new THREE.DirectionalLight(0xffffff, 0.55 * sh.keyMul)
    if (cameraRelativeKey) {
      keyLight = key
      keyTarget = new THREE.Object3D(); scene.add(keyTarget)
      key.target = keyTarget
    } else {
      key.position.set(1, 1.4, 1) // legacy world-fixed key (packaging label)
    }
    scene.add(key)
    // Fill stays world-fixed and weak — it only lifts the shadow side; making it
    // camera-relative too would flatten the form again.
    const fill = new THREE.DirectionalLight(0xffffff, 0.30 * sh.keyMul); fill.position.set(-1.2, -0.4, -0.6); scene.add(fill)
    scene.add(new THREE.HemisphereLight(0xffffff, 0x444444, sh.hemi))
  }

  // Perspective (product-shot look for the packaging label) vs orthographic (anatomical
  // report views — no foreshortening). Perspective distance = 3× radius → mild depth.
  const cam = perspective
    ? new THREE.PerspectiveCamera(30, 1, radius * 0.05, radius * 40)
    : new THREE.OrthographicCamera(-radius, radius, radius, -radius, 0.01, radius * 20)
  const margin = 1.12

  // Ambient-occlusion (GTAO) post-process — crevice shadows (BPR look). Built once;
  // the passes hold a reference to `cam`, which we mutate per view, so per-view
  // projection updates propagate automatically. World-space radius scaled to the mesh.
  let composer = null
  if (shadows) {
    const { EffectComposer, RenderPass, GTAOPass, OutputPass } = await postFx()
    composer = new EffectComposer(renderer)
    composer.setSize(res, res)
    composer.addPass(new RenderPass(scene, cam))
    const gtao = new GTAOPass(scene, cam, res, res)
    gtao.output = GTAOPass.OUTPUT.Default // beauty × AO
    gtao.blendIntensity = aoIntensity
    gtao.updateGtaoMaterial({ radius: radius * aoRadius, distanceExponent: 1.0, thickness: 1.0, scale: 1.6, samples: 16, screenSpaceRadius: false })
    composer.addPass(gtao)
    composer.addPass(new OutputPass())
  }

  // ONE shared ortho scale for every view (see the viewFit block in shoot()): the
  // largest projected half-extent over all 6 anatomical views. Computed with a
  // throwaway camera so it does not disturb the render camera's state. ISO is
  // excluded — it is rendered separately and its diagonal extent would shrink the
  // six-view plate. Only meaningful for the orthographic viewFit path; the
  // Packaging Illustration (perspective, no viewFit) never reaches this.
  let sharedFitScale = 0
  if (!perspective && viewFit) {
    const probe = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.01, radius * 20)
    const rt = new THREE.Vector3(), upv = new THREE.Vector3(), fw = new THREE.Vector3()
    const t = new THREE.Vector3()
    for (const [, def] of VIEW_DEFS) {
      const d = new THREE.Vector3(...def.dir).normalize()
      probe.position.copy(center).addScaledVector(d, radius * 3)
      probe.up.set(...def.up)
      probe.lookAt(center)
      probe.updateMatrixWorld(true)
      probe.matrixWorld.extractBasis(rt, upv, fw)
      let hw = 0, hh = 0
      for (const c of corners) { t.subVectors(c, center); hw = Math.max(hw, Math.abs(t.dot(rt))); hh = Math.max(hh, Math.abs(t.dot(upv))) }
      sharedFitScale = Math.max(sharedFitScale, Math.max(hw, hh) * 1.05)
    }
  }
  // An explicit override always wins, so every scene in a fit group is framed
  // identically. The derived value is still returned, so a caller can run the
  // two-pass fit.
  const derivedFitScale = sharedFitScale
  if (fitScaleOverride != null) sharedFitScale = fitScaleOverride

  // ── Relief mask pass ──────────────────────────────────────────────────────────────
  // A second render whose ONLY output is the multiplier for the colour frame:
  //   1.0 where nothing self-shadows, (1 - reliefStrength) where fully shadowed.
  // Not a lit render — a lit pass carries broad diffuse falloff and would darken one whole
  // side of the skull. This shader ignores light colour and intensity and reads the shadow
  // term alone, so the only thing it can express is geometry shadowing itself: crevices,
  // sutures, and the embossed lettering. Composited with 'multiply', so an unshadowed pixel
  // multiplies by exactly 1.0 and cannot change.
  //
  // MeshMatcapMaterial cannot do this itself: it is not a `lights: true` material, so three
  // never binds the shadow uniforms to it. Hence a separate one-purpose material.
  let maskMat = null, maskCanvas = null, origMats = null
  if (useReliefShadow) {
    maskMat = new THREE.ShaderMaterial({
      lights: true,
      side,
      uniforms: THREE.UniformsUtils.merge([THREE.UniformsLib.lights]),
      vertexShader: `
        #include <common>
        #include <shadowmap_pars_vertex>
        void main() {
          #include <beginnormal_vertex>
          #include <defaultnormal_vertex>
          #include <begin_vertex>
          #include <project_vertex>
          #include <worldpos_vertex>
          #include <shadowmap_vertex>
        }
      `,
      fragmentShader: `
        #include <common>
        #include <packing>
        #include <lights_pars_begin>
        #include <shadowmap_pars_fragment>
        #include <shadowmask_pars_fragment>
        void main() {
          // RAW shadow amount, 0 = lit, 1 = fully shadowed. Deliberately NOT the final
          // multiplier: the frame has to be high-passed first (see stampView). A skull
          // casts big shadows onto its own flank and onto the mandible, and multiplying
          // those in darkens half the anatomy — measured on the real case, the first
          // attempt blackened the left parietal and the whole mandible while the letters
          // read perfectly. Only the LOCAL excess of shadow is engraving.
          gl_FragColor = vec4( vec3( 1.0 - getShadowMask() ), 1.0 );
        }
      `,
    })
    maskCanvas = document.createElement('canvas')
    maskCanvas.width = res; maskCanvas.height = res
    origMats = meshes.map((m) => m.material)
  }

  // Letter-edge pass. Renders ONLY the marked stamp meshes, as view-space normals, so the
  // compositor can keep the local excess and darken the engraved outlines (see letterEdges).
  //
  // Driven by the SAME reliefStrength as the lit rig rather than by a second slider. The
  // panel's own note explains why: "zero already means off, and a second control to reason
  // about is how this panel got churned in the first place." One number means how strongly
  // the stamp reads, and at 0 neither half of it does anything.
  // `reliefShadow` IS THE CONSENT, not reliefStrength. REPORT_LOOK ships reliefStrength 0.55
  // with reliefShadow false, and renderScenes' REPORT_RENDER_OPTS spreads REPORT_LOOK without
  // going through ReportLookPanel — so gating on strength alone armed this pass at 0.55 in the
  // Case Report Builder on any scene where detectLabelMesh happened to find a plate, with
  // nobody asking for it. That also matches what the option already says of itself: strength
  // "is where that slider starts from, not a value that applies while reliefShadow is false".
  //
  // THE MARK, not `labelMesh`. `labelMesh` is the marked group OR detectLabelMesh's guess when
  // nothing was marked, and this pass has no shape test precisely because the operator's choice
  // IS the answer — so running it on a guess asserts a premise that is not true. Not
  // hypothetical: the Case Report Builder never passes `stampGroup`, so there the mesh is
  // always guessed, and detectLabelMesh will pick a thin dense splint that resolveStampMeshes
  // refuses outright as `non-anatomy`. Tracing a rim around an appliance on a delivered case
  // report, with nothing marked and nobody asking, is the failure this feature exists to avoid.
  const edgeStrength = (stamp.reason === 'marked' && reliefShadow)
    ? clamp01(reliefStrength) : 0
  let edgeMat = null, occluderMat = null, edgeCanvas = null
  // Per-view outcome of edgeDecision, collected so a refusal reaches the operator. `edgeCall`
  // is this view's; `edgeSeen` is every view, which is what the summary below is built from.
  let edgeCall = null
  const edgeSeen = []
  if (edgeStrength > 0) {
    edgeMat = new THREE.MeshNormalMaterial({ side })
    // Depth only, no colour: the rest of the model must be able to hide the stamp without
    // appearing in the mask itself. `side` matches the colour pass so a culled back face
    // cannot occlude in one pass and not the other.
    occluderMat = new THREE.MeshBasicMaterial({ colorWrite: false, side })
    edgeCanvas = document.createElement('canvas')
    edgeCanvas.width = res; edgeCanvas.height = res
  }

  const shoot = (def, code) => {
    const dir = new THREE.Vector3(...def.dir).normalize()
    cam.position.copy(center).addScaledVector(dir, radius * 3)
    cam.up.set(...def.up)
    cam.lookAt(center)
    if (keyLight) {
      // place the key light relative to THIS view's camera (upper-left-front by default)
      // so every view is lit consistently. localDir: +Z toward camera, +Y up, -X left.
      const az = lightAzimuth * Math.PI / 180, el = lightElevation * Math.PI / 180
      const ld = new THREE.Vector3(-Math.sin(az) * Math.cos(el), Math.sin(el), Math.cos(az) * Math.cos(el))
      ld.applyQuaternion(cam.quaternion) // camera-space → world (object → light)
      keyLight.position.copy(center).addScaledVector(ld, radius * 3)
      keyTarget.position.copy(center)
    }
    if (perspective) {
      // Fill the frame to the mesh's projected 2D extent (scale-up) while KEEPING
      // perspective: project the 8 AABB corners to NDC at zoom 1, then set zoom so the
      // widest fills ~92% of the frame.
      cam.zoom = 1; cam.updateMatrixWorld(true); cam.updateProjectionMatrix()
      let maxAbs = 0
      const v = new THREE.Vector3()
      for (const c of corners) { v.copy(c).project(cam); maxAbs = Math.max(maxAbs, Math.abs(v.x), Math.abs(v.y)) }
      cam.zoom = 0.92 / (maxAbs || 1)
      cam.updateProjectionMatrix()
    } else {
      let s = radius * margin
      if (viewFit) {
        // ONE SHARED SCALE across all views (sharedFitScale, computed once below).
        // Sizing each view to its OWN projected extent made the laterals render ~2.5×
        // larger than A/P/S/I — anatomically misleading in a 6-view plate and unlike
        // the ZBrush reference, which uses a single scale. Falls back to the per-view
        // fit only if the shared pass somehow produced nothing.
        cam.updateMatrixWorld(true)
        const rt = new THREE.Vector3(), upv = new THREE.Vector3(), fw = new THREE.Vector3()
        cam.matrixWorld.extractBasis(rt, upv, fw)
        let hw = 0, hh = 0
        const t = new THREE.Vector3()
        for (const c of corners) { t.subVectors(c, center); hw = Math.max(hw, Math.abs(t.dot(rt))); hh = Math.max(hh, Math.abs(t.dot(upv))) }
        // ISO FITS ITSELF. `sharedFitScale` is the largest half-extent over the
        // six ANATOMICAL views — the comment where it is computed states that
        // ISO is excluded from it, and it is, but the scale was then applied to
        // ISO anyway. An isometric looks down a diagonal, where a model projects
        // wider than in any axis-aligned view, so the ISO overflowed its frame
        // and the camera cut off whatever fell outside.
        //
        // Measured on a real skull before the fix: A/S/LL sat 21-177px clear of
        // every edge while ISO touched two of them at 0px. Reported from the
        // field as "why is the image getting cropped at the bottom? We need to
        // resize if it does not fit — not crop it". Which edge is lost depends
        // on how the model happens to lie along the diagonal.
        //
        // The six-view plate keeps ONE shared scale, so it stays anatomically
        // comparable. The ISO is a standalone illustration that nothing is
        // measured against, so it also ignores fitScaleOverride: an override
        // exists to make a fit GROUP comparable, and cropping is too high a
        // price for that.
        s = (code === 'ISO' ? 0 : sharedFitScale) || (Math.max(hw, hh) * 1.05)
      }
      cam.left = -s; cam.right = s; cam.top = s; cam.bottom = -s
      cam.near = 0.01; cam.far = radius * 20
      cam.updateProjectionMatrix()
    }
    // Mask FIRST, into its own canvas, so the colour frame is never copied and the
    // existing colour path below stays exactly as it was.
    if (maskMat) {
      for (const m of meshes) m.material = maskMat
      // Opaque BLACK clear: the pass now emits shadow AMOUNT, so 0 outside the model.
      renderer.setClearColor(0x000000, 1)
      renderer.render(scene, cam)          // never the composer — GTAO must not touch the mask
      const mctx = maskCanvas.getContext('2d')
      mctx.clearRect(0, 0, res, res)
      mctx.drawImage(canvas, 0, 0, res, res)
      for (let i = 0; i < meshes.length; i++) meshes[i].material = origMats[i]
      renderer.setClearColor(bgColor, shadows ? 0 : 1)
    }
    // Letter edges: the STAMP MESHES ALONE, as normals, on a transparent clear so alpha
    // carries coverage and the compositor can tell interior from silhouette. Everything else
    // is hidden rather than filtered afterwards, because a normal edge between the stamp and
    // the bone behind it is indistinguishable from a letter wall once it is in the image.
    if (edgeMat) {
      const wasMat = meshes.map((m) => m.material)
      const wasOrder = meshes.map((m) => m.renderOrder)
      for (const m of meshes) {
        const isStamp = labelSet.has(m)
        // THE REST OF THE MODEL STAYS IN THE PASS, writing depth but no colour, so the mask
        // agrees with what the camera can actually see. Hiding it instead — the first attempt
        // — left nothing to occlude the stamp, and the case ID appeared straight through the
        // forehead in the ANTERIOR view, mirrored. Caught in the rendered report; no test
        // looked at a view the stamp should be absent from.
        m.material = isStamp ? edgeMat : occluderMat
        // BOTH HALVES ARE GUARDED by tools/render-hash `reportStampHidden`, which moves under
        // three separate mutations: removing the occluder, zeroing the renderOrder, and hiding
        // the other meshes. An earlier attempt at that config asserted nothing and was deleted;
        // it failed because the probe was an OPEN shell, so with backface culling nothing wrote
        // depth from behind and no view could occlude anything. The fixture is closed now.
        //
        // Measured on the REAL case when this was found — Anterior view, edge pass on vs off,
        // pixels changed by more than 5 levels: hiding the model 22,796; occluder without
        // renderOrder 22,796 (the mask for Anterior held 24,984 stamp pixels against
        // Posterior's 24,959, i.e. the fix looked right and did nothing); occluder with
        // renderOrder 490, against a 422 control of the same frame rendered twice.
        //
        // ORDER IS LOAD-BEARING, and this is the part that is easy to get wrong twice.
        // `colorWrite: false` cannot ERASE colour that is already in the buffer — it only
        // declines to write its own. three sorts opaque draws by material, so the stamp was
        // being drawn first and the occluder's later depth write could not undo it: the fix
        // looked right, the render was unchanged, and the mask for Anterior still held 24,984
        // stamp pixels against Posterior's 24,959. Occluders must go DOWN FIRST.
        m.renderOrder = isStamp ? 1 : 0
      }
      renderer.setClearColor(0x000000, 0)
      renderer.render(scene, cam)          // never the composer — GTAO must not touch the mask
      const ectx = edgeCanvas.getContext('2d')
      ectx.clearRect(0, 0, res, res)
      ectx.drawImage(canvas, 0, 0, res, res)
      for (let i = 0; i < meshes.length; i++) {
        meshes[i].material = wasMat[i]; meshes[i].renderOrder = wasOrder[i]
      }
      renderer.setClearColor(bgColor, shadows ? 0 : 1)
      // Decide HERE, not in the compositor, so the answer can be reported. maskShape returns
      // early on an empty mask, which is most views — the stamp is on the occiput, so five of
      // six views see nothing and used to pay a full 2.56M-pixel neighbour scan to find out.
      edgeCall = { code, ...edgeDecision(maskShape(ectx.getImageData(0, 0, res, res).data, res, res)) }
      edgeSeen.push(edgeCall)
    }
    if (composer) composer.render(); else renderer.render(scene, cam)
    return stampView(canvas, {
      lightBg: !dark,
      bg: bgCss,
      cornerLabel: cornerLabels ? code : '',
      subLabel: cornerLabels ? (VIEW_LABELS[code] || '') : '',
      watermark,
      reliefMask: maskCanvas,
      reliefStrength: maskMat ? clamp01(reliefStrength) : 0,
      edgeMask: edgeCanvas,
      edgeStrength,
      edgeOutlinePx: edgeCall?.draw ? edgeCall.outlinePx : 0,
    })
  }

  // Frame handling lives in resolveViewDef (exported, tested): flipAP swaps the A/P camera
  // sides for RAS meshes; lpsFrame moves the lateral cameras and rights S/I for LPS meshes
  // (the planning engine's exports) so every badge names the side it shows.
  const frame = { flipAP, lpsFrame }
  const wanted = views && views.length ? new Set(views.map((v) => v.toUpperCase())) : null
  const out = {}
  const ortho = []
  for (const [code, def] of VIEW_DEFS) {
    if (wanted && !wanted.has(code)) continue
    const b64 = shoot(resolveViewDef(def, frame, code), code)
    out[code] = b64
    ortho.push(b64)
  }
  let iso = ''
  if (!wanted || wanted.has('ISO')) {
    iso = shoot(resolveViewDef(ISO_DEF, frame, 'ISO'), 'ISO')  // isometric — labelled like the rest
    out.ISO = iso
  }
  // Extra cameras, only when ASKED FOR. `!wanted` (render everything)
  // deliberately does not include these: that path is what the packaging label
  // and the STL/OBJ tool take, and they must keep emitting exactly the views
  // they always have.
  //
  // An entry is either a built-in code or a camera the CALLER supplies —
  // `{ code, dir, up }`. The second form exists because which view of the
  // anatomy a figure needs is domain knowledge: it belongs beside the page
  // recipe that wants it, not hardcoded in the renderer. A supplied camera wins
  // over a built-in of the same code, so a template can override one.
  for (const entry of extraViews) {
    const code = typeof entry === 'string' ? entry : entry?.code
    if (!code) continue
    const def = (typeof entry === 'object' && Array.isArray(entry.dir))
      ? { dir: entry.dir, up: entry.up ?? [0, 0, 1] }
      : EXTRA_VIEW_DEFS[code]
    if (!def) continue
    if (!wanted || wanted.has(code)) out[code] = shoot(resolveViewDef(def, frame, code), code)
  }

  composer?.dispose?.()
  renderer.dispose()
  // dispose() frees the GPU resources but NOT the WebGL context itself, and a
  // browser caps live contexts (~16 in Chrome). One render per page never hit
  // that; the Case Report Builder makes ONE PER SCENE PER PASS — 58 for the
  // 29-scene reference corpus — and the tab died outright ("Target page,
  // context or browser has been closed") once GTAO's extra buffers were added.
  //
  // forceContextLoss() is three.js's documented way to hand the context back.
  // It runs AFTER every view has been captured to a data URL, so it cannot
  // affect a single pixel — the render hashes prove that.
  renderer.forceContextLoss?.()
  return {
    views: out, ortho, iso,
    labels: Object.fromEntries(Object.keys(out).map((c) => [c, VIEW_LABELS[c]])),
    metrics,
    // Whether the engraved-stamp relief actually engaged. See the `relief` block above:
    // this is reported so a sheet with an invisible stamp cannot look like a correct one.
    //
    // `edge` is the same promise for the letter outline. A refusal is INVISIBLE in the output —
    // the page looks like a correct one and the lettering simply reads faintly — which is the
    // exact failure reliefStatus was written to end, so it is not allowed to happen quietly a
    // second time. `drawn` counts views where the outline was applied; `refused` names the
    // reason for the views the stamp was visible in but declined, and is null when there is
    // nothing to say (no stamp marked, or every view that saw it got its outline).
    relief,
    edge: edgeSummary(edgeSeen),
    // What this scene WOULD have been framed at on its own — the input to a
    // two-pass fit across a group of scenes that must share one scale.
    fitScale: derivedFitScale,
  }
}
